/**
 * 收款链接（invoice）。
 *
 * 商户手工开一张单 → 拿到一条公开链接 → 发给付款人 → 付款人自己打开付款。
 * 适合没有做技术对接、但需要收一笔款的场景：报价单、尾款、线下补单。
 *
 * ## 链接就是凭据
 *
 * 拿到链接的人可以付这笔款。它**不能**改金额、不能改收款方、读不到商户的任何其它数据 ——
 * 所以泄露的最坏后果是「有人替你付了钱」。即便如此，界面上也要把它当凭据对待：
 * 复制而不是明文平铺，并且说清楚它意味着什么。
 *
 * ## 为什么这里不做「重发」
 *
 * 链接是固定的，重发等于再复制一次。做一个看起来会「重新生成」的按钮，
 * 会让人以为旧链接失效了，而实际上并没有 —— 那比不做更糟。要作废就作废。
 */

import { useState } from 'react';
import { request } from '../lib/api.ts';
import { usePagedList, useMutation, useAsync } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Button, Card, CopyId, DetailGrid, ErrorBox, Field, ModeBadge, Modal,
  Money, Select, StatusBadge, TextInput,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatMinor, toMinor } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Invoice, Mode } from '../lib/types.ts';

const STATUSES = [
  { value: 'open', label: '待付款' },
  { value: 'paid', label: '已付款' },
  { value: 'void', label: '已作废' },
  { value: 'expired', label: '已过期' },
];

/** 单张链接允许的付款尝试上限，与网关的 `MAX_ATTEMPTS` 一致。 */
const MAX_ATTEMPTS = 8;

export function InvoicesPage() {
  const session = useSession();
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Invoice | null>(null);

  const list = usePagedList<Invoice>('/v1/invoices', {
    status: status === '' ? null : status,
    mode: mode === '' ? null : mode,
  });

  // 只有商户收款；平台/代理是替下级开单，所以按钮始终给，由后端判管辖权
  const canCreate = session.can('admin') && session.me.org.status === 'active';

  const columns: readonly Column<Invoice>[] = [
    { key: 'title', header: '名目', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.title}</span>
        <span className="cell-muted mono" style={{ fontSize: 11 }}>{row.order_no}</span>
      </div>
    ) },
    ...(session.isMerchant ? [] : [{
      key: 'merchant',
      header: '商户',
      render: (row: Invoice) => <span>{row.merchant_name ?? row.merchant_org_id}</span>,
    }]),
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    { key: 'mode', header: '模式', render: (row) => <ModeBadge mode={row.mode} /> },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'attempts', header: '尝试', numeric: true, render: (row) => (
      <span className={row.attempt_count >= MAX_ATTEMPTS ? 'num dr' : 'num cell-muted'}>
        {row.attempt_count} / {MAX_ATTEMPTS}
      </span>
    ) },
    { key: 'expires', header: '有效期', render: (row) => (
      <span className="cell-muted mono">
        {row.expires_at === null ? '长期' : formatDateTime(row.expires_at)}
      </span>
    ) },
    { key: 'created', header: '创建时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>收款链接</h1>
        <span style={{ flex: 1 }} />
        {canCreate && <Button variant="primary" onClick={() => { setCreating(true); }}>开一张单</Button>}
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">开完单会得到一条链接，发给谁谁就能付</span>
        <span>
          付款人不需要账号。链接里的金额、收款方由服务端锁定，
          <strong>任何人都改不了</strong>；单张链接的付款尝试上限是 {MAX_ATTEMPTS} 次，超了会自动锁定。
        </span>
      </div>

      <div className="filters">
        <Field label="状态">
          <Select value={status} onChange={setStatus} placeholder="全部" options={STATUSES} />
        </Field>
        <Field label="模式">
          <Select
            value={mode}
            onChange={setMode}
            placeholder="全部"
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => { setSelected(row); }}
          empty="还没有收款链接"
          emptyHint="适合没做技术对接、但需要收一笔款的场景：报价单、尾款、线下补单。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {creating && (
        <CreateInvoiceModal
          onClose={() => { setCreating(false); }}
          onDone={(invoice) => { setCreating(false); list.reload(); setSelected(invoice); }}
        />
      )}
      {selected !== null && (
        <InvoiceModal
          invoice={selected}
          onClose={() => { setSelected(null); }}
          onChanged={() => { list.reload(); setSelected(null); }}
        />
      )}
    </>
  );
}

function CreateInvoiceModal({
  onClose, onDone,
}: {
  onClose: () => void;
  onDone: (invoice: Invoice) => void;
}) {
  const session = useSession();
  /*
   * 上级替下级开单。
   *
   * 收款链接的钱必须落到某个**商户**账上（通道是按商户绑定的），而平台与代理
   * 自己不是商户。接口一直支持传 `merchant_org_id`，界面上却没有这个字段 ——
   * 于是平台/代理填完一整张表单、点了创建，才被服务端拒掉。
   *
   * 商户身份不需要选：只能给自己开，传了别人的也会被越权检查挡下。
   */
  const merchants = useAsync<{ data: { id: string; name: string; status: string }[] }>(
    session.isMerchant ? null : '/v1/orgs',
    { query: { type: 'merchant', limit: 200 } },
    [session.isMerchant],
  );
  const merchantOptions = (merchants.data?.data ?? [])
    .filter((org) => org.status === 'active')
    .map((org) => ({ value: org.id, label: org.name }));
  const [merchantOrgId, setMerchantOrgId] = useState('');
  const needsMerchant = !session.isMerchant;
  const merchantMissing = needsMerchant && merchantOrgId === '';
  /*
   * 列表**没读到**和「你名下确实没有商户」是两回事。
   *
   * 之前只分了 loading / 空列表两支：`/v1/orgs` 一旦出错（会话过期、网关抖动、
   * 权限不足），页面就笃定地告诉你「你下面还没有可收款的商户」并把创建按钮永久禁用 ——
   * 一个明明有十个商户的代理，会被一条网络错误劝去重新建组织。
   * 出错时要说出错，并且给一条重试的路。
   */
  const merchantsFailed = needsMerchant && !merchants.loading && merchants.error !== null;

  const [mode, setMode] = useState<Mode>('test');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [expiresHours, setExpiresHours] = useState('72');
  const [amountError, setAmountError] = useState<string | null>(null);

  const create = useMutation(async () => {
    // 金额在本地就转成最小单位：小数位超限这类错误当场暴露，不用发一次请求再被拒
    const minor = toMinor(amount, currency);
    if (minor <= 0n) throw new Error('金额必须大于 0');
    return await request<Invoice>('/v1/invoices', {
      method: 'POST',
      body: {
        ...(merchantOrgId === '' ? {} : { merchant_org_id: merchantOrgId }),
        mode,
        amount: minor.toString(),
        currency: currency.toUpperCase(),
        title,
        ...(description === '' ? {} : { description }),
        ...(email === '' ? {} : { customer_email: email }),
        ...(expiresHours === '' ? {} : { expires_in_hours: Number(expiresHours) }),
      },
    });
  });

  return (
    <Modal
      title="开一张收款单"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={
              create.pending ||
              title.trim() === '' ||
              amount === '' ||
              amountError !== null ||
              // 没选收款商户就点不动 —— 让人填完一整张表再被服务端拒掉是最差的顺序
              merchantMissing
            }
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(result); }); }}
          >
            {create.pending ? '创建中…' : '创建并取链接'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      {needsMerchant && (
        merchants.loading ? (
          <Field label="收款商户"><span className="field-hint">正在读取商户列表…</span></Field>
        ) : merchantsFailed ? (
          <div className="alert" data-tone="bad">
            <span className="alert-title">商户列表没读出来</span>
            <span>
              这不代表你名下没有商户 —— 是这次请求失败了。请重试；一直不行的话多半是
              登录态过期了，退出重新登录即可。
            </span>
            <div style={{ marginTop: 10 }}>
              <Button onClick={() => { merchants.reload(); }}>重试</Button>
            </div>
          </div>
        ) : merchantOptions.length === 0 ? (
          <div className="alert" data-tone="warn">
            <span className="alert-title">你下面还没有可收款的商户</span>
            <span>
              收款链接的钱要落到某个<strong>商户</strong>账上 —— 通道是按商户绑定的，
              平台与代理自己不收款。请先到「组织」建一个商户，并在「MID 下发」里给它绑一条通道。
            </span>
          </div>
        ) : (
          <Field
            label="收款商户"
            hint="这笔钱落到哪个商户账上。走哪条通道由该商户绑定的通道决定，开单时不指定。"
          >
            <Select
              value={merchantOrgId}
              onChange={setMerchantOrgId}
              placeholder="请选择"
              options={merchantOptions}
            />
          </Field>
        )
      )}

      <Field label="名目" hint="付款人会在页面最显眼的位置看到它">
        <TextInput value={title} onChange={setTitle} placeholder="例如：年度订阅 · 专业版" />
      </Field>

      <div className="grid cols-2">
        <Field label="金额" hint={`按 ${currency} 的正常写法填，例如 129.00`}>
          <TextInput
            value={amount}
            numeric
            onChange={(value) => {
              setAmount(value);
              if (value === '') { setAmountError(null); return; }
              try {
                toMinor(value, currency);
                setAmountError(null);
              } catch (cause) {
                setAmountError(cause instanceof Error ? cause.message : '金额不合法');
              }
            }}
          />
        </Field>
        <Field label="币种">
          <TextInput value={currency} onChange={(value) => { setCurrency(value.toUpperCase()); }} />
        </Field>
      </div>

      {amountError !== null && (
        <div className="alert" data-tone="warn"><span className="alert-title">{amountError}</span></div>
      )}

      <Field label="模式" hint="test 单只会走 test 通道，不产生真实扣款。">
        <Select
          value={mode}
          onChange={(value) => { setMode(value as Mode); }}
          options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
        />
      </Field>

      {mode === 'live' && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">这是 live 单</span>
          <span>拿到链接的人会被真实扣款。确认金额无误再创建 —— 金额创建后不可修改。</span>
        </div>
      )}

      <Field label="说明" hint="选填。会显示在付款页上，适合写清楚这笔钱包含什么。">
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => { setDescription(event.target.value); }}
          placeholder="2026-08-18 至 2027-08-17，含 5 个席位与优先支持"
        />
      </Field>

      <div className="grid cols-2">
        <Field label="付款人邮箱" hint="选填，仅用于展示与回执，不做校验">
          <TextInput value={email} onChange={setEmail} type="email" />
        </Field>
        <Field label="有效期（小时）" hint="留空表示长期有效">
          <TextInput value={expiresHours} onChange={setExpiresHours} numeric />
        </Field>
      </div>
    </Modal>
  );
}

function InvoiceModal({
  invoice, onClose, onChanged,
}: {
  invoice: Invoice;
  onClose: () => void;
  onChanged: () => void;
}) {
  const session = useSession();
  const [copied, setCopied] = useState(false);

  const voidIt = useMutation(async () =>
    await request<Invoice>(`/v1/invoices/${invoice.id}/void`, { method: 'POST' }));

  const canVoid = session.can('admin') && invoice.status === 'open';

  return (
    <Modal
      title={invoice.title}
      onClose={onClose}
      wide
      footer={
        canVoid ? (
          <>
            <Button
              variant="danger"
              disabled={voidIt.pending}
              onClick={() => { void voidIt.run().then((result) => { if (result !== null) onChanged(); }); }}
            >
              {voidIt.pending ? '作废中…' : '作废这张单'}
            </Button>
            <span style={{ flex: 1 }} />
            <Button onClick={onClose}>关闭</Button>
          </>
        ) : undefined
      }
    >
      <ErrorBox error={voidIt.error} />

      <div className="grid cols-3">
        <div className="metric">
          <span className="metric-label">金额</span>
          <span className="metric-value">
            {formatMinor(invoice.amount, invoice.currency)}
            <span className="metric-currency">{invoice.currency}</span>
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">状态</span>
          <span style={{ marginTop: 4 }}><StatusBadge status={invoice.status} /></span>
        </div>
        <div className="metric">
          <span className="metric-label">付款尝试</span>
          <span className={invoice.attempt_count >= MAX_ATTEMPTS ? 'metric-value bad' : 'metric-value'}>
            {invoice.attempt_count}
            <span className="metric-currency">/ {MAX_ATTEMPTS}</span>
          </span>
        </div>
      </div>

      {invoice.status === 'open' && (
        <div className="stack tight">
          <span className="field-label">付款链接</span>
          <span className="field-hint">
            <strong>这条链接等于一张可以付款的凭据</strong> —— 拿到的人能付这笔钱。
            它改不了金额、改不了收款方，也读不到你的其它任何数据，但仍然只该发给该付款的人。
          </span>
          <div className="secret-box">{invoice.pay_url}</div>
          <div className="row">
            <Button
              variant="primary"
              onClick={() => {
                void navigator.clipboard.writeText(invoice.pay_url).then(() => {
                  setCopied(true);
                  setTimeout(() => { setCopied(false); }, 1600);
                });
              }}
            >
              {copied ? '已复制到剪贴板' : '复制链接'}
            </Button>
            <a className="btn" href={invoice.pay_url} target="_blank" rel="noreferrer">
              预览付款页 ↗
            </a>
          </div>
        </div>
      )}

      <DetailGrid rows={[
        ['单号', <CopyId key="id" id={invoice.id} full />],
        ['订单号', <span key="o" className="mono">{invoice.order_no}</span>],
        ['模式', <ModeBadge key="m" mode={invoice.mode} />],
        invoice.merchant_name === undefined ? null
          : ['收款商户', <span key="mc">{invoice.merchant_name}</span>],
        invoice.description === null ? null
          : ['说明', <span key="d" style={{ whiteSpace: 'pre-wrap' }}>{invoice.description}</span>],
        invoice.customer_email === null ? null
          : ['付款人邮箱', <span key="e" className="mono">{invoice.customer_email}</span>],
        ['有效期', <span key="x" className="mono">
          {invoice.expires_at === null ? '长期有效' : formatDateTime(invoice.expires_at)}
        </span>],
        ['创建时间', <span key="c" className="mono">{formatDateTime(invoice.created_at)}</span>],
        invoice.paid_at === null ? null
          : ['付款时间', <span key="p" className="mono">{formatDateTime(invoice.paid_at)}</span>],
        invoice.payment_id === null ? null
          : ['关联交易', <CopyId key="pay" id={invoice.payment_id} full />],
      ]} />

      {invoice.status === 'paid' && (
        <div className="alert" data-tone="good">
          <span className="alert-title">已收到款</span>
          <span>这笔钱已经进入你的待结算余额，会在通道结算周期后转为可用。需要退款请到交易页发起。</span>
        </div>
      )}

      {invoice.attempt_count >= MAX_ATTEMPTS && invoice.status === 'open' && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">这张单已被锁定</span>
          <span>
            付款尝试已达上限。这个上限是防止有人拿公开链接反复试卡号 ——
            如果确实是付款人自己没付成，请作废后重新开一张。
          </span>
        </div>
      )}

      {canVoid && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">作废后链接立即失效</span>
          <span>已经发出去的链接会显示「已被商户作废」。已付款的单不能作废。</span>
        </div>
      )}
    </Modal>
  );
}
